import React from "react";
import styled from "styled-components";
import { Bookmark } from "lucide-react";
import { router } from "@inertiajs/react";

import { apiGet, apiDelete } from "../../../api";
import { LoggedInUserContext } from "../../../providers/ProvideLoggedInUser";
import LeftSidebar from "../../components/left-sidebar";
import TrendingPanel from "../../components/trending-panel";
import { renderContent } from "../../utils/render-content";
import { AppLayout, FeedColumn, RightSidebar } from "../../constants/styles";

const PageHeader = styled.div`
  padding: 1rem;
  border-bottom: 1px solid var(--border);
  h1 {
    font-size: 1.25rem;
    font-weight: 700;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem 1rem;
  color: var(--muted-foreground);
  h3 {
    font-size: 1.125rem;
    margin-bottom: 0.5rem;
  }
`;

const ThreadCard = styled.article`
  padding: 1rem;
  border-bottom: 1px solid var(--border);
  cursor: pointer;
  &:hover {
    background: var(--hover-bg, rgba(0, 0, 0, 0.03));
  }
`;

const AuthorRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
`;

const Avatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: var(--primary, #ff9900);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-weight: 700;
`;

const Name = styled.span`
  font-weight: 700;
`;
const Handle = styled.span`
  color: var(--muted-foreground);
  font-size: 0.875rem;
`;
const Timestamp = styled.span`
  color: var(--muted-foreground);
  font-size: 0.875rem;
`;
const Content = styled.div`
  margin-bottom: 0.5rem;
  line-height: 1.5;
`;

const Actions = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 0.5rem;
`;

const ActionBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  color: ${(p) => (p.$active ? "var(--primary)" : "var(--muted-foreground)")};
  font-size: 0.875rem;
  &:hover {
    color: var(--primary);
  }
`;

export default function BookmarksPage() {
  React.use(LoggedInUserContext);
  const [threads, setThreads] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [page, setPage] = React.useState(1);
  const [hasMore, setHasMore] = React.useState(false);

  const loadBookmarks = React.useCallback((p = 1) => {
    setLoading(true);
    apiGet(`/api/v1/communities/bookmarks?page=${p}&perpage=20`)
      .then((res) => {
        if (res.parsedResponse?.data) {
          if (p === 1) setThreads(res.parsedResponse.data);
          else setThreads((prev) => [...prev, ...res.parsedResponse.data]);
          const meta = res.parsedResponse.meta;
          setPage(meta?.page ?? p);
          setHasMore(meta ? meta.page < meta.pages : false);
        }
      })
      .finally(() => setLoading(false));
  }, []);

  React.useEffect(() => {
    loadBookmarks(1);
  }, []);

  const handleUnbookmark = async (threadId) => {
    setThreads((prev) => prev.filter((t) => t.id !== threadId));
    await apiDelete(`/api/v1/communities/threads/${threadId}/bookmarks`);
  };

  return (
    <AppLayout>
      <LeftSidebar activePage="bookmarks" />
      <FeedColumn>
        <PageHeader>
          <h1>Bookmarks</h1>
        </PageHeader>
        {loading && threads.length === 0 ? (
          <EmptyState>Loading...</EmptyState>
        ) : threads.length === 0 ? (
          <EmptyState>
            <Bookmark size={48} />
            <h3>No bookmarks yet</h3>
            <p>When you bookmark a post, it will show up here.</p>
          </EmptyState>
        ) : (
          <>
            {threads.map((thread) => (
              <ThreadCard
                key={thread.id}
                onClick={() =>
                  router.visit(`/communities/threads/${thread.id}`)
                }
              >
                <AuthorRow>
                  <Avatar>{thread.author?.name?.charAt(0) || "?"}</Avatar>
                  <Name>{thread.author?.name}</Name>
                  <Handle>{thread.author?.handle}</Handle>
                  <span style={{ color: "var(--muted-foreground)" }}>·</span>
                  <Timestamp>{thread.timestamp}</Timestamp>
                </AuthorRow>
                <Content>{renderContent(thread.content)}</Content>
                <Actions>
                  <ActionBtn
                    $active
                    onClick={(e) => {
                      e.stopPropagation();
                      handleUnbookmark(thread.id);
                    }}
                  >
                    <Bookmark size={16} fill="currentColor" /> Remove
                  </ActionBtn>
                </Actions>
              </ThreadCard>
            ))}
            {hasMore && (
              <button
                onClick={() => loadBookmarks(page + 1)}
                style={{
                  width: "100%",
                  padding: "1rem",
                  background: "none",
                  border: "none",
                  color: "var(--primary)",
                  cursor: "pointer",
                  fontWeight: 600,
                }}
              >
                Load more
              </button>
            )}
          </>
        )}
      </FeedColumn>
      <RightSidebar>
        <TrendingPanel />
      </RightSidebar>
    </AppLayout>
  );
}
